import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '@/components/feature/Footer';
import type { UserProfile } from '@/lib/types';
import { getMemberProfiles } from '@/services/userService';

type RoleFilter = 'all' | 'admin' | 'moderator' | 'member' | 'banned';

function roleMeta(roles: string[]): { label: string; isAdmin: boolean; isModerator: boolean } {
  const list = (roles ?? []).map((r) => r.toLowerCase());
  const isAdmin = list.some((r) => r === 'admin' || r === 'administrator' || r === 'administrateur');
  const isModerator = list.some((r) => r === 'moderator' || r === 'modérateur' || r === 'modo');
  if (isAdmin) return { label: 'Administrateur', isAdmin: true, isModerator: false };
  if (isModerator) return { label: 'Modérateur', isAdmin: false, isModerator: true };
  return { label: 'Membre', isAdmin: false, isModerator: false };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const TYPE_META: Record<string, { icon: string; className: string }> = {
  SOCIAL: { icon: 'ri-group-line', className: 'bg-background-100 text-foreground-600' },
  SPORT: { icon: 'ri-basketball-line', className: 'bg-secondary-100 text-secondary-900' },
  HELP_REQUEST: { icon: 'ri-hand-heart-line', className: 'bg-accent-100 text-accent-900' },
  HELP_OFFER: { icon: 'ri-hand-heart-line', className: 'bg-accent-100 text-accent-900' },
};

export default function MembersPage() {
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [selected, setSelected] = useState<UserProfile | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMemberProfiles();
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((u) => {
      const name = (u.displayName ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      const city = (u.city ?? '').toLowerCase();
      const meta = roleMeta(u.roles);
      if (q && !name.includes(q) && !email.includes(q) && !city.includes(q)) return false;
      if (roleFilter === 'admin' && !meta.isAdmin) return false;
      if (roleFilter === 'moderator' && !meta.isModerator) return false;
      if (roleFilter === 'member' && (meta.isAdmin || meta.isModerator)) return false;
      if (roleFilter === 'banned' && !u.isBanned) return false;
      return true;
    });
  }, [members, search, roleFilter]);

  const adminCount = members.filter((u) => roleMeta(u.roles).isAdmin).length;
  const moderatorCount = members.filter((u) => roleMeta(u.roles).isModerator).length;
  const bannedCount = members.filter((u) => u.isBanned).length;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-background-200">
        <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Retour
            </Link>
            <Link to="/" className="flex items-center cursor-pointer bg-white rounded-lg px-2 py-1">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20flat%20design%20logo%20on%20pure%20white%20background%2C%20a%20warm%20terracotta%20map%20location%20pin%20containing%20three%20dark%20brown%20human%20silhouettes%20standing%20together%2C%20next%20to%20elegant%20serif%20typography%20spelling%20On%20fait%20quoi%3F%20in%20dark%20brown%20color%20with%20a%20warm%20amber%20orange%20question%20mark%20and%20decorative%20curved%20underline%20accent%2C%20clean%20minimalist%20vector%20style%2C%20high%20contrast%2C%20crisp%20edges%2C%20professional%20brand%20identity%20design%2C%20no%20shadows%2C%20no%20gradients%2C%20pure%20flat%20colors%2C%20harmonious%20warm%20earth%20tone%20palette&width=800&height=800&seq=logo-YonYon-v2&orientation=squarish"
                alt="Yonyon"
                className="h-10 w-auto"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center text-lg font-bold">
              <i className="ri-team-line text-xl"></i>
            </span>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                Les membres
              </h1>
              <p className="text-sm text-foreground-500">
                {members.length} personnes inscrites · {adminCount} admin · {moderatorCount} modérateur{moderatorCount > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Search + filters */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email ou ville…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200 bg-white text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div className="inline-flex rounded-full bg-background-100 p-1 self-start flex-wrap">
              {[
                { value: 'all', label: 'Tous' },
                { value: 'admin', label: 'Admins' },
                { value: 'moderator', label: 'Modos' },
                { value: 'member', label: 'Membres' },
                { value: 'banned', label: `Bannis (${bannedCount})` },
              ].map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setRoleFilter(f.value as RoleFilter)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${roleFilter === f.value
                    ? 'bg-white text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-background-100"></div>
                      <div className="flex-1">
                        <div className="h-3 bg-background-100 rounded w-28 mb-2"></div>
                        <div className="h-3 bg-background-100 rounded w-40"></div>
                      </div>
                    </div>
                    <div className="mt-4 h-3 bg-background-100 rounded w-full"></div>
                    <div className="mt-2 h-3 bg-background-100 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-white border border-background-200 rounded-lg p-8 text-center">
                <p className="text-sm text-foreground-500">{error}</p>
                <button
                  type="button"
                  onClick={load}
                  className="mt-3 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Réessayer
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                  <i className="ri-user-search-line text-2xl"></i>
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  Aucun membre
                </h3>
                <p className="mt-1 text-sm text-foreground-500">Aucun résultat pour cette recherche.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((u) => (
                  <MemberCard key={u.id} member={u} onClick={() => setSelected(u)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {selected && <ProfileModal member={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MemberCard({ member, onClick }: { member: UserProfile; onClick: () => void }) {
  const meta = roleMeta(member.roles);
  const initials = (member.displayName ?? '')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-background-200 rounded-lg p-5 hover:border-primary-300 transition-colors cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        <img
          src={member.avatarUrl}
          alt={member.displayName}
          className="w-14 h-14 rounded-full object-cover object-top shrink-0 bg-background-100"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
              {member.displayName}
            </p>
            {meta.isAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 text-[11px] font-semibold">
                <i className="ri-vip-crown-line text-xs"></i>
                Admin
              </span>
            )}
            {meta.isModerator && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold">
                <i className="ri-shield-star-line text-xs"></i>
                Modo
              </span>
            )}
            {member.isBanned && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold">
                <i className="ri-forbid-2-line text-xs"></i>
                Banni
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-400 truncate mt-0.5">{member.city}</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-foreground-600 line-clamp-2">{member.bio}</p>

      <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <i className="ri-compass-3-line text-sm"></i>
          {member.activitiesCount} activités
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-calendar-check-line text-sm"></i>
          {member.participationsCount} participations
        </span>
      </div>
    </button>
  );
}

function ProfileModal({ member, onClose }: { member: UserProfile; onClose: () => void }) {
  const meta = roleMeta(member.roles);
  const initials = (member.displayName ?? '')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onClose} aria-hidden="true"></div>
      <div className="relative w-full max-w-lg bg-white border border-background-200 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Header */}
        <div className="px-6 py-5 bg-background-50 border-b border-background-200 sm:rounded-t-lg">
          <div className="flex items-start gap-4">
            <img
              src={member.avatarUrl}
              alt={member.displayName}
              className="w-20 h-20 rounded-full object-cover object-top shrink-0 bg-background-100"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">{member.displayName}</h2>
                {meta.isAdmin && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 text-[11px] font-semibold">
                    <i className="ri-vip-crown-line text-xs"></i>
                    Administrateur
                  </span>
                )}
                {meta.isModerator && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold">
                    <i className="ri-shield-star-line text-xs"></i>
                    Modérateur
                  </span>
                )}
                {member.isBanned && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold">
                    <i className="ri-forbid-2-line text-xs"></i>
                    Banni
                  </span>
                )}
              </div>
              <p className="text-sm text-foreground-500 mt-0.5">{member.email}</p>
              <p className="text-xs text-foreground-400 mt-1">
                <i className="ri-map-pin-2-line text-xs"></i> {member.city} · inscrit·e le {formatDate(member.createdAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-50 border border-background-200 rounded-lg p-3 text-center">
              <p className="font-heading text-xl font-semibold text-foreground-950">{member.activitiesCount}</p>
              <p className="text-xs text-foreground-500 mt-0.5">Activités</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-3 text-center">
              <p className="font-heading text-xl font-semibold text-foreground-950">{member.participationsCount}</p>
              <p className="text-xs text-foreground-500 mt-0.5">Participations</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-3 text-center">
              <p className="font-heading text-xl font-semibold text-foreground-950">{initials}</p>
              <p className="text-xs text-foreground-500 mt-0.5">Membre</p>
            </div>
          </div>

          {/* Bio */}
          <div className="mt-5">
            <h4 className="font-heading text-sm font-semibold text-foreground-950">À propos</h4>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
          </div>

          {member.isBanned && member.banReason && (
            <div className="mt-4 bg-accent-50 border border-accent-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-accent-900 flex items-center gap-1.5">
                <i className="ri-forbid-2-line text-sm"></i>
                Banni
              </p>
              <p className="text-xs text-accent-900/80 mt-1">Motif : {member.banReason}</p>
              {member.bannedUntil && (
                <p className="text-xs text-accent-900/80 mt-0.5">Jusqu'au {formatDate(member.bannedUntil)}</p>
              )}
            </div>
          )}

          {/* Activities */}
          <div className="mt-5">
            <h4 className="font-heading text-sm font-semibold text-foreground-950">Activités récentes</h4>
            {member.activities.length === 0 ? (
              <p className="mt-2 text-sm text-foreground-400">Aucune activité pour le moment.</p>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {member.activities.map((a) => {
                  const t = TYPE_META[a.type] ?? TYPE_META.SOCIAL;
                  return (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-3"
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${t.className}`}>
                        <i className={`${t.icon} text-sm`}></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-950 truncate">{a.title}</p>
                        <p className="text-xs text-foreground-400">{a.time}</p>
                      </div>
                      <span className="text-[11px] font-semibold text-foreground-500 whitespace-nowrap">
                        {a.role === 'organisateur' ? 'Organisateur' : 'Participant'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}